/**
 * Build-identity helpers shared by every path that may reload the tab.
 *
 * The deploy tag (git short SHA) is baked into index.html (``VITE_BUILD_ID``,
 * see lib/html-config.ts) and served alongside it as ``/version.json``; the api pod
 * ships the same tag on the realtime ``ready`` envelope. Comparing them answers
 * the one question every self-healing reload path needs to ask first:
 * **is the server actually running a different build than this tab?**
 *
 * Before KAN-196 nobody asked. A failed chunk fetch was treated as "stale
 * build" outright, so a dropped connection (Wi-Fi switch, laptop wake, one of
 * the ~2 requests/minute Cloudflare aborts against our origin) hard-reloaded
 * the page out from under the user, mid-work, with no deploy anywhere in sight.
 */

import { htmlConfig } from '@/lib/html-config';

const VERSION_URL = '/version.json';

/**
 * Hard cap on the check. A *stalled* request is the dangerous shape here, not a
 * refused one: callers block their UI on this answer, so without a deadline a
 * connection that neither completes nor fails would leave the user staring at a
 * dead page forever — the very failure mode this whole change exists to remove.
 */
const FETCH_TIMEOUT_MS = 3_000;

function timeoutSignal(): AbortSignal | undefined {
  // Guarded: AbortSignal.timeout is missing in older engines and in some test
  // environments; without it we simply fall back to no deadline here (the
  // caller-side safety timer still covers the UI).
  if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') {
    return undefined;
  }
  return AbortSignal.timeout(FETCH_TIMEOUT_MS);
}

/** Baked-in build id; undefined in local dev → version checks disable themselves. */
export function clientBuildId(): string | undefined {
  return htmlConfig('build-id');
}

/**
 * Fetch the build id the server is currently serving. Returns null when it
 * cannot be determined (offline, non-JSON dev response, unexpected shape) —
 * callers must treat null as "don't know", never as "differs".
 *
 * ``no-store`` is load-bearing: a cached response would pin a stale build id at
 * the CDN and the tab would never learn about a new deploy.
 */
export async function fetchServerBuildId(): Promise<string | null> {
  try {
    const res = await fetch(VERSION_URL, { cache: 'no-store', signal: timeoutSignal() });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    const buildId = (data as { buildId?: unknown } | null)?.buildId;
    return typeof buildId === 'string' && buildId ? buildId : null;
  } catch {
    // Network error, timeout, or dev where /version.json resolves to
    // index.html and JSON.parse throws.
    return null;
  }
}

/**
 * True only when we positively know a *different* build is being served.
 *
 * Deliberately conservative — unknown on either side answers false, so an
 * unreachable ``/version.json`` (exactly the situation during the network
 * blips this guard exists for) can never justify a reload.
 */
export async function serverBuildDiffers(): Promise<boolean> {
  const own = clientBuildId();
  if (!own) return false;
  const server = await fetchServerBuildId();
  if (!server) return false;
  return server !== own;
}
